import { useMemo } from 'react';
import { PromoCreateDto, PromoUpdateDto } from '@libs/dto';
import { promoApi, emptyResponse } from '@carcareers/store';
import { useConfirmation, useLoading, useSnackbar } from '@carcareers/contexts';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { ApiError, ApiResponse } from '@libs/types';
import { Promo } from '@libs/models';

interface PromoCallbacks {
  onSuccess?: () => void;
}

export const usePromo = (id?: number) => {
  const { setLoading, setLabel } = useLoading();
  const { confirm } = useConfirmation();
  const { toggleSnackbar } = useSnackbar();
  const [promoCreate] = promoApi.usePromoCreateMutation();
  const [promoUpdate] = promoApi.usePromoUpdateMutation();
  const [promoDelete] = promoApi.usePromoDeleteMutation();
  const [promoIncrementUses] = promoApi.usePromoIncrementUsesMutation();
  const [promoActivate] = promoApi.usePromoActivateMutation();
  const [promoDeactivate] = promoApi.usePromoDeactivateMutation();
  const indexPromos = promoApi.usePromoIndexQuery({ page: 1, limit: 10 });
  const viewPromo = id !== undefined ? promoApi.usePromoViewQuery(id) : undefined;

  return useMemo(
    () => ({
      indexPromos,
      viewPromo,
      createPromo: async (promo?: PromoCreateDto, callbacks?: PromoCallbacks): Promise<ApiResponse<Promo>> => {
        if (!promo) {
          toggleSnackbar('warning', 'Promo data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating promo...');
        setLoading(true);
        const response = await promoCreate(promo);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Promo created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      updatePromo: async (promo?: PromoUpdateDto, callbacks?: PromoCallbacks): Promise<ApiResponse<Promo>> => {
        if (!promo) {
          toggleSnackbar('warning', 'Promo data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating promo...');
        setLoading(true);
        const response = await promoUpdate(promo);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Promo updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      incrementPromoUses: async (id?: number, callbacks?: PromoCallbacks) => {
        if (!id) return;

        setLabel('Updating promo uses...');
        setLoading(true);
        const response = await promoIncrementUses(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', 'Promo uses incremented');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else if (getErrors(response).length !== 0) {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        return response;
      },
      deletePromo: async (id?: number, callbacks?: PromoCallbacks) => {
        if (!id) return;

        const confirmed = await confirm({
          title: 'Delete Promo',
          message: 'Are you sure you want to delete this promo?',
        });

        if (!confirmed) return;

        setLabel('Deleting promo...');
        setLoading(true);

        const response = await promoDelete(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', `Deleted promo ${id}`);
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          toggleSnackbar('error', 'This promo has been used and cannot be deleted.');
        }

        return response;
      },
      activatePromo: async (id?: number, callbacks?: PromoCallbacks) => {
        if (!id) return;

        setLabel('Activating promo...');
        setLoading(true);
        const response = await promoActivate(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', 'Promo activated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else if (getErrors(response).length !== 0) {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        return response;
      },
      deactivatePromo: async (id?: number, callbacks?: PromoCallbacks) => {
        if (!id) return;

        setLabel('Deactivating promo...');
        setLoading(true);
        const response = await promoDeactivate(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', 'Promo deactivated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else if (getErrors(response).length !== 0) {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        return response;
      },
      validatePromoCode: (code?: string) => (code ? promoApi.usePromoValidateQuery(code) : emptyResponse),
    }),
    [
      indexPromos,
      viewPromo,
      setLabel,
      setLoading,
      promoCreate,
      toggleSnackbar,
      promoUpdate,
      promoIncrementUses,
      confirm,
      promoDelete,
      promoActivate,
      promoDeactivate,
    ],
  );
};
